import React from 'react';
import classNames from 'classnames';
import {
    useClpPurchaseContext,
    CLPPurchaseOption,
} from '../../context/clp-purchase-context.react-component';
import {useI18n} from '@udemy-v2/i18n';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import styles from './course-sidebar-container.module.scss';
import {CollapsibleBuyBox} from '../buy-box/collapsible-buy-box.react-component';
import {CollapsibleSubscriptionPurchaseContent} from '@/features/course-landing-page/components/subscription-purchase-content/collapsible-subscription-purchase-content.react-component';
import {CollapsibleNonCatalogPPContent} from '@/features/course-landing-page/components/non-catalog-pp-offer';
import {ClpIconSecondaryActions} from '@/features/course-landing-page/components/clp-secondary-actions/clp-icon-secondary-actions.react-component';
import {GrowWithGoogleEnrollmentBox} from '@/features/course-landing-page/components/grow-with-google-enrollment-box';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';
import {useFeatures} from '@udemy-v2/react-experimentation';

export const CourseSidebarCollapsibleActionsContainerSkeleton = () => {
    return (
        <div className={styles.container}>
            <TextSkeleton
                className={styles.skeleton}
                withTitle={true}
                lineCountPerParagraph={3}
                paragraphCount={4}
            />
        </div>
    );
};

/**
 * CourseSidebarCollapsibleActionsContainer component - this renders for enrolled, free, and non-Personal Plan paid courses
 * @constructor
 */
export const CourseSidebarCollapsibleActionsContainer = ({}) => {
    const {gettext} = useI18n();
    const {
        isLoading,
        selectedPurchaseOption,
        isCourseInAvailablePlans,
        isEligibleForNonCatalogPPOffer,
        isCourseIncludedInGrowWithGoogleSubscription,
    } = useClpPurchaseContext();

    const isSubscriptionPrimary = selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;
    const {featureVariantAssignments: features} = useFeatures([
        'show_subscription_copy_in_clp_price_box',
    ]);
    const isSubCopyEnabled = features.get('show_subscription_copy_in_clp_price_box');
    const showSubCopy = isSubCopyEnabled?.configuration?.showSubCopyComponent === true;

    const subscriptionContent = isCourseInAvailablePlans && (
        <CollapsibleSubscriptionPurchaseContent
            isSubCopyEnabled={showSubCopy}/>
    );

    const hasMultiplePurchaseOptions = isCourseInAvailablePlans || isEligibleForNonCatalogPPOffer;
    const transactionalContent = <CollapsibleBuyBox showRadio={hasMultiplePurchaseOptions} />;

    let primaryContent: React.ReactNode;
    let secondaryContent: React.ReactNode;

    if (isEligibleForNonCatalogPPOffer) {
        const nonCatalogSubsContent = (
            <CollapsibleNonCatalogPPContent
                isSubCopyEnabled={showSubCopy}
            />
        );
        primaryContent = isSubscriptionPrimary ? nonCatalogSubsContent : transactionalContent;
        secondaryContent = isSubscriptionPrimary ? transactionalContent : nonCatalogSubsContent;
    } else if (isCourseInAvailablePlans) {
        primaryContent = isSubscriptionPrimary ? subscriptionContent : transactionalContent;
        secondaryContent = isSubscriptionPrimary ? transactionalContent : subscriptionContent;
    } else {
        primaryContent = transactionalContent;
        secondaryContent = null;
    }

    if (isLoading) {
        return <CourseSidebarCollapsibleActionsContainerSkeleton />;
    }
    return (
        <>
            {isCourseIncludedInGrowWithGoogleSubscription ? (
                <div className={classNames(styles.container)}>
                    <GrowWithGoogleEnrollmentBox placement={CourseContentPlacement.SIDEBAR} />
                </div>
            ) : (
                <>
                    <span className={classNames(styles['collapsible-title'], 'ud-heading-xl')}>
                        {gettext('Purchase options')}
                    </span>
                    <div className={styles['collapsible-container']}>
                        <div>
                            {primaryContent}
                            {secondaryContent}
                        </div>
                        <div>
                            <ClpIconSecondaryActions
                                className={styles['collapsible-secondary-actions']}
                            />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
